import { FC, useState } from "react";
import { RefCallBack, useController } from "react-hook-form";
import { FaQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import PropTypes from "prop-types";
import { Input, Typography } from "antd";
import "./CBInput.scss";
import "../CBShared.style.scss";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";

interface CBInputTypes {
  label: string;
  name: string;
  control: any;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  tooltipMessage?: string;
  helperLink?: string;
  caption?: string;
  onBlur?: any;
  onChange?: any;
  className?: string;
  maxLength?: number;
  addonAfter?: React.ReactNode;
  suffix?: string;
}
interface InputPropsTypes extends CBInputTypes {
  "id"?: string;
  "className"?: string;
  "status"?: "error" | "warning" | undefined;
  "autoComplete"?: string;
  "ref"?: RefCallBack;
  "value"?: string;
  "aria-invalid"?: "true" | "false" | undefined;
}

const CBInput: FC<CBInputTypes> = ({
  label,
  name,
  control,
  type,
  required = false,
  disabled = false,
  placeholder = "",
  tooltipMessage = "",
  helperLink = "",
  caption = "",
  onBlur = () => {},
  onChange = () => {},
  className,
  maxLength,
  addonAfter,
  suffix,
}) => {
  const { field, fieldState } = useController({ name, control });
  const [requiredState, setRequiredState] = useState(false);
  const id = `input-${field.name}`;
  const { Text } = Typography;
  const inputProps: InputPropsTypes = {
    "className": "text-sm py-1.5",
    "id": id,
    "status": requiredState ? "error" : undefined,
    "autoComplete": "off",
    "type": type ? type : "text",
    "placeholder": placeholder,
    "disabled": disabled,
    "ref": field.ref,
    "name": field.name,
    "value": field.value,
    "onBlur": (e: any) => {
      setRequiredState(required && !e.target.value.length ? true : false);
      field.onBlur();
      onBlur(e);
    },
    "onChange": (e: any) => {
      setRequiredState(required && !e.target.value.length ? true : false);
      field.onChange(e);
      onChange(e);
    },
    "aria-invalid": fieldState.error ? "true" : "false",
    "label": "",
    "control": undefined,
    "addonAfter": addonAfter,
    ...(maxLength && { maxLength: maxLength }),
    "suffix": suffix,
  };

  return (
    <div className={`CBInput my-2.5 relative text-left ${className}`}>
      <div className="container">
        <label style={{ display: "flex" }} htmlFor={id}>
          {label && (
            <div className="flex gap-1">
              <Text
                style={{ marginBottom: 0, whiteSpace: "nowrap" }}
              >{`${label}`}</Text>
              <Text
                type="danger"
                style={{ marginBottom: 0 }}
              >{`${required ? " *" : ""}`}</Text>
            </div>
          )}
          {helperLink && (
            <a
              style={{ textDecoration: "none" }}
              href={helperLink}
              rel="noreferrer"
              target="_blank"
            >
              (Help)
            </a>
          )}
          {tooltipMessage && (
            <>
              <span
                data-tooltip-id={`${id}-tooltip`}
                data-tooltip-content={tooltipMessage}
                className="tooltip"
              >
                <FaQuestionCircle />
              </span>
              <Tooltip id={`${id}-tooltip`} />
            </>
          )}
        </label>
      </div>
      {type === "password" ? (
        <Input.Password
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
          {...inputProps}
        />
      ) : (
        <Input {...inputProps} />
      )}
      <div className="custom-helper">
        {caption && <Text type="secondary">{caption}</Text>}
        {!!fieldState.error && (
          <Text role="alert" className="text-red-500">
            {fieldState.error.message}
          </Text>
        )}
      </div>
    </div>
  );
};

CBInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  tooltipMessage: PropTypes.string,
  helperLink: PropTypes.string,
  caption: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
};

export default CBInput;
